import React, { useMemo, useState } from 'react';
import { MapGeometry, AvailableMap } from '../../services/mapsApi';
import { COUNTRIES, mapCountries } from '../../config/countries';
import type { CountryCode } from '../../config/countries';
import { pickerLayers } from '../../config/mapLayers';

interface MapSelectComponentProps {
  selectedMapType: string;
  onMapTypeChange: (mapTypeId: string) => void;
  selectedGrids: MapGeometry[];
  onGridRemove: (gridId: string) => void;
  onGridsExport: () => void;
  onAddById?: (ids: string[]) => Promise<void>;
  availableMaps: AvailableMap[];
  loadingMaps?: boolean;
  countryCode: CountryCode;
  onCountryChange: (code: CountryCode) => void;
}

const MapSelectComponent: React.FC<MapSelectComponentProps> = ({
  selectedMapType,
  onMapTypeChange,
  selectedGrids,
  onGridRemove,
  onGridsExport,
  onAddById,
  availableMaps,
  loadingMaps = false,
  countryCode,
  onCountryChange,
}) => {
  const [idInput, setIdInput] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // The maps API's list says which layers exist; the registry names them and says what they are used for.
  const layers = useMemo(() => pickerLayers(countryCode, availableMaps), [countryCode, availableMaps]);

  const handleAddById = async () => {
    if (!idInput.trim() || !selectedMapType || !onAddById) return;

    setIsAdding(true);
    try {
      const ids = idInput
        .split(/[,;\s]+/)
        .map((id) => id.trim())
        .filter((id) => id.length > 0);

      if (ids.length > 0) {
        await onAddById(ids);
        setIdInput('');
      }
    } catch (error) {
      console.error('Failed to add grids by ID:', error);
      alert(`Failed to add grids: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsAdding(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddById();
    }
  };

  return (
    <div className="w-80 bg-surface border-l border-paper-300 flex flex-col h-full">
      <div className="p-4 border-b border-paper-300">
        <h2 className="text-lg font-semibold text-ink-soft mb-2">Map Layer Selection</h2>
        <p className="text-sm text-ink-muted">
          Choose the administrative or statistical boundaries to display on the map
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Country selector */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-ink-soft mb-2">Country</h3>
          <div className="flex gap-2">
            {mapCountries().map(({ code, label, flag }) => (
              <button
                key={code}
                onClick={() => onCountryChange(code)}
                className={`flex-1 py-1.5 text-sm rounded border transition-colors ${
                  countryCode === code
                    ? 'bg-brand-500 text-on-brand border-brand-500'
                    : 'bg-surface text-ink-soft border-paper-400 hover:border-brand-500'
                }`}
              >
                {flag} {label}
              </button>
            ))}
          </div>
        </div>

        {/* Map type list */}
        <div className="space-y-3">
          {loadingMaps ? (
            <div className="flex items-center gap-2 text-sm text-ink-subtle">
              <svg className="animate-spin h-4 w-4 text-brand-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Loading map types...
            </div>
          ) : layers.length === 0 ? (
            <div className="text-sm text-ink-subtle">No maps available for {COUNTRIES[countryCode].label} yet.</div>
          ) : (
            layers.map((layer) => (
              <div key={layer.geoType} className="border border-paper-300 rounded-lg p-3">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="mapType"
                    value={layer.geoType}
                    checked={selectedMapType === layer.geoType}
                    onChange={(e) => onMapTypeChange(e.target.value)}
                    className="mt-1 h-4 w-4 text-brand-500 focus:ring-brand-500 border-paper-400"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-ink">{layer.label}</div>
                    {layer.description ? <div className="text-sm mt-1 text-ink-subtle">{layer.description}</div> : null}
                    <ul className="mt-1 space-y-0.5 text-xs text-ink-muted">
                      {layer.uses.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </div>
                </label>
              </div>
            ))
          )}
        </div>

        {/* Selected Grids Section */}
        <div className="mt-6">
          <div className="border border-paper-300 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-ink">Selected Areas</h3>
              {selectedGrids.length > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-ok-soft text-ok-ink">
                  {selectedGrids.length}
                </span>
              )}
            </div>

            {selectedMapType && onAddById && (
              <div className="mb-3">
                <label className="block text-xs text-ink-muted mb-1">Add by ID(s)</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={idInput}
                    onChange={(e) => setIdInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter ID(s), comma separated"
                    className="flex-1 px-2 py-1 text-xs bg-surface border border-paper-400 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                    disabled={isAdding}
                  />
                  <button
                    onClick={handleAddById}
                    disabled={!idInput.trim() || isAdding}
                    className="px-3 py-1 text-xs bg-brand-500 text-on-brand rounded hover:bg-brand-600 disabled:bg-paper-400 disabled:cursor-not-allowed"
                  >
                    {isAdding ? '...' : 'Add'}
                  </button>
                </div>
              </div>
            )}

            {selectedGrids.length === 0 ? (
              <div className="text-xs text-ink-subtle">
                No areas selected.{' '}
                {selectedMapType ? 'Click on map areas or add by ID above.' : 'Select a map type first.'}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="max-h-48 overflow-y-auto">
                  {selectedGrids.map((grid) => (
                    <div
                      key={grid.geo_id}
                      className="flex items-center justify-between bg-paper-100 p-2 rounded"
                    >
                      <span className="text-xs font-mono text-ink-soft">{grid.geo_id}</span>
                      <button
                        onClick={() => onGridRemove(grid.geo_id)}
                        className="text-bad-ink hover:bg-bad-soft rounded px-1"
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={onGridsExport}
                  className="w-full px-3 py-2 text-sm bg-brand-500 text-on-brand rounded hover:bg-brand-600"
                >
                  Save Areas ({selectedGrids.length})
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 border-t border-paper-300 bg-paper-100">
        <div className="text-xs text-ink-subtle">
          Selected:{' '}
          <span className="font-medium text-ink-soft">
            {layers.find((l) => l.geoType === selectedMapType)?.label || 'None'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default MapSelectComponent;
